// src/pages/StudentDashboard.jsx — v4c
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Box, Grid, Paper, Typography, Stack, Button, Chip,
  CircularProgress, LinearProgress, Avatar, Divider, Skeleton,
} from "@mui/material";
import SchoolIcon      from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon  from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon      from "@mui/icons-material/Cancel";
import PlayArrowIcon   from "@mui/icons-material/PlayArrow";
import API             from "../api";
import KpiCard         from "../components/KpiCard";
import ApiError        from "../components/ApiError";
import { useApi }      from "../components/useApi";
import { P, pctColor, getPct } from "../theme";

// ── Score ring ────────────────────────────────────────────────────────────────
function ScoreRing({ pct, label, size = 96 }) {
  const color = pctColor(pct);
  return (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress variant="determinate" value={100} size={size} thickness={4.5}
          sx={{ color: P.slate[100], position: "absolute" }} />
        <CircularProgress variant="determinate" value={Math.min(pct, 100)} size={size} thickness={4.5}
          sx={{ color }} />
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontWeight: 800, color, fontSize: size * .22, lineHeight: 1 }}>
            {Math.round(pct)}%
          </Typography>
        </Box>
      </Box>
      <Typography sx={{
        display: "block", mt: .75, fontSize: "0.68rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.07em", color: P.slate[500],
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function StudentDashboard() {
  const { t, i18n } = useTranslation();
  const [attempts, setAttempts] = useState([]);
  const [exams,    setExams]    = useState([]);
  const { loading, error, run, clearError } = useApi();

  // Titre de page
  useEffect(() => { document.title = "Mon espace — ExamLabs"; }, []);

  const userInfo = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user_info") || "{}"); }
    catch { return {}; }
  }, []);
  const name = userInfo.full_name || userInfo.username || t("studentDashboard.student");

  useEffect(() => {
    run(async () => {
      const [a, e] = await Promise.all([
        API.get("/attempts/?page=1&page_size=200"),
        API.get("/exams/?page_size=100"),
      ]);
      const att = Array.isArray(a.data) ? a.data : (a.data.items || []);
      const exs = Array.isArray(e.data) ? e.data : (e.data.items || []);
      setAttempts(att);
      setExams(exs);
    });
  }, []); // eslint-disable-line

  // ── Calculs stats ───────────────────────────────────────────────────────────
  const total   = attempts.length;
  const pcts    = useMemo(() => attempts.map(getPct), [attempts]);
  const avgPct  = total ? Math.round(pcts.reduce((s, v) => s + v, 0) / total) : 0;
  const bestPct = total ? Math.max(...pcts) : 0;
  const passN   = pcts.filter(p => p >= 50).length;
  const passRate = total ? Math.round((passN / total) * 100) : 0;

  const recent  = useMemo(() =>
    [...attempts].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)).slice(0, 5),
  [attempts]);

  const triedIds  = useMemo(() => new Set(attempts.map(a => a.exam_id)), [attempts]);
  const available = useMemo(() => exams.filter(e => !triedIds.has(e.id)).slice(0, 4), [exams, triedIds]);

  const lastByExam = useMemo(() => {
    const m = new Map();
    [...attempts].sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
      .forEach(a => m.set(a.exam_id, getPct(a)));
    return m;
  }, [attempts]);

  const examProgress = useMemo(() =>
    exams.filter(e => lastByExam.has(e.id)).map(e => ({ ...e, pct: lastByExam.get(e.id) })),
  [exams, lastByExam]);

  const examTitle = useMemo(() => {
    const map = new Map(exams.map(e => [e.id, e.title]));
    return id => map.get(id) || `Examen #${id}`;
  }, [exams]);

  return (
    <Box>
      {/* ── Bannière ── */}
      <Box sx={{
        borderRadius: "16px", background: P.navy, color: "white",
        p: { xs: 3, md: 5 }, mb: 4, position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -40, right: -40, width: 160, height: 160,
          borderRadius: "50%", border: "2px solid rgba(255,255,255,.08)" }} />
        <Box sx={{ position: "absolute", bottom: -60, right: 80, width: 120, height: 120,
          borderRadius: "50%", border: "2px solid rgba(255,255,255,.05)" }} />
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.1em", color: "rgba(255,255,255,.5)", mb: .5 }}>
          {t("studentDashboard.label")}
        </Typography>
        <Typography sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, fontWeight: 800, mb: 2.5 }}>
          {t("studentDashboard.greeting")} <Box component="span" sx={{ color: "#a5f3fc" }}>{name}</Box> 👋
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button component={Link} to="/exams" variant="contained" startIcon={<SchoolIcon />}
            sx={{ background: "rgba(255,255,255,.15)", color: "white",
              border: "1px solid rgba(255,255,255,.25)", textTransform: "none", fontWeight: 700,
              borderRadius: "8px", "&:hover": { background: "rgba(255,255,255,.25)" } }}>
            {t("studentDashboard.takeExam")}
          </Button>
          <Button component={Link} to="/results" variant="outlined"
            sx={{ borderColor: "rgba(255,255,255,.3)", color: "white", textTransform: "none",
              fontWeight: 600, borderRadius: "8px", "&:hover": { borderColor: "white" } }}>
            {t("studentDashboard.myResults")}
          </Button>
        </Stack>
      </Box>

      {/* Erreur réseau */}
      <ApiError message={error} onRetry={() => { clearError(); window.location.reload(); }} />

      <Grid container spacing={3}>
        {/* ── KPIs ── */}
        {[
          { icon: <SchoolIcon />,      color: P.navy,  label: t("studentDashboard.kpi.attempts"),  value: total,         sub: t("studentDashboard.kpi.attemptsSub") },
          { icon: <TrendingUpIcon />,  color: P.teal,  label: t("studentDashboard.kpi.avgScore"),  value: `${avgPct}%`,  sub: t("studentDashboard.kpi.avgScoreSub") },
          { icon: <EmojiEventsIcon />, color: P.amber, label: t("studentDashboard.kpi.bestScore"), value: `${bestPct}%`, sub: t("studentDashboard.kpi.bestScoreSub") },
          { icon: <CheckCircleIcon />, color: P.green, label: t("studentDashboard.kpi.passRate"),  value: `${passRate}%`,sub: t("studentDashboard.kpi.passRateSub") },
        ].map(k => (
          <Grid item xs={6} md={3} key={k.label}>
            <KpiCard {...k} loading={loading} />
          </Grid>
        ))}

        {/* ── Progression par examen ── */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: `1px solid ${P.slate[200]}`, height: "100%" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 2, color: P.slate[800] }}>
              {t("studentDashboard.progressByExam")}
            </Typography>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={40} sx={{ borderRadius: 1, mb: 1 }} />
              ))
            ) : examProgress.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t("studentDashboard.noExamsTaken")}
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {examProgress.map(e => {
                  const col = pctColor(e.pct);
                  return (
                    <Box key={e.id}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: .75 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "70%" }}>
                          {e.title}
                        </Typography>
                        <Typography variant="body2" fontWeight={800} sx={{ color: col }}>
                          {e.pct}%
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={Math.min(e.pct, 100)}
                        sx={{ height: 8, borderRadius: 4, background: P.slate[100],
                          "& .MuiLinearProgress-bar": { background: col, borderRadius: 4 } }} />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* ── Vue d'ensemble ── */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: `1px solid ${P.slate[200]}`, height: "100%" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 2, color: P.slate[800] }}>
              {t("studentDashboard.overview")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-around", mt: 3 }}>
              {loading ? (
                <>
                  <Skeleton variant="circular" width={96} height={96} />
                  <Skeleton variant="circular" width={96} height={96} />
                </>
              ) : (
                <>
                  <ScoreRing pct={avgPct}   label={t("studentDashboard.kpi.avgScore")} />
                  <ScoreRing pct={passRate} label={t("studentDashboard.ringPass")}    />
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* ── Dernières tentatives ── */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: `1px solid ${P.slate[200]}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: P.slate[800] }}>
                {t("studentDashboard.recentAttempts")}
              </Typography>
              <Button size="small" component={Link} to="/results"
                sx={{ color: P.navy, textTransform: "none", fontWeight: 600 }}>
                {t("studentDashboard.seeAll")}
              </Button>
            </Stack>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={52} sx={{ borderRadius: 1, mb: .5 }} />
              ))
            ) : recent.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t("studentDashboard.noAttempts")}
                </Typography>
                <Button variant="contained" component={Link} to="/exams"
                  sx={{ background: P.navy, textTransform: "none", fontWeight: 700 }}>
                  {t("studentDashboard.startFirstTest")}
                </Button>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {recent.map(a => {
                  const pct    = getPct(a);
                  const passed = pct >= 50;
                  const col    = passed ? P.green : P.red;
                  return (
                    <Stack key={a.id} direction="row" alignItems="center"
                      justifyContent="space-between" sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, fontSize: "0.78rem", fontWeight: 800,
                          background: `${col}15`, color: col, border: `1.5px solid ${col}30` }}>
                          {pct}%
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {examTitle(a.exam_id)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(a.submitted_at).toLocaleDateString(i18n.language === "en" ? "en-US" : "fr-FR", {
                              day: "numeric", month: "short",
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        icon={passed
                          ? <CheckCircleIcon sx={{ fontSize: "0.85rem !important" }} />
                          : <CancelIcon     sx={{ fontSize: "0.85rem !important" }} />}
                        label={passed ? t("common.passed") : t("common.failed")} size="small"
                        sx={{ background: `${col}12`, color: col, fontWeight: 700,
                          "& .MuiChip-icon": { color: col } }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* ── À essayer ── */}
        {!loading && available.length > 0 && (
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: "12px", border: `1px solid ${P.slate[200]}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: P.slate[800], mb: 2 }}>
                {t("studentDashboard.toTry")}
              </Typography>
              <Stack spacing={1.5}>
                {available.map(e => (
                  <Stack key={e.id} direction="row" alignItems="center" justifyContent="space-between"
                    sx={{ p: 1.75, borderRadius: "8px", border: `1px solid ${P.slate[200]}`,
                      transition: "all .15s",
                      "&:hover": { borderColor: P.navy, background: P.slate[50] } }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{e.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.duration_minutes ? `${e.duration_minutes} ${t("common.min")}` : t("studentDashboard.freeDuration")}
                      </Typography>
                    </Box>
                    <Button size="small" component={Link} to={`/exams/${e.id}`}
                      endIcon={<PlayArrowIcon />} variant="outlined"
                      sx={{ color: P.navy, borderColor: P.navy, textTransform: "none",
                        fontWeight: 700, borderRadius: "6px" }}>
                      {t("studentDashboard.start")}
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}